import { v4 as uuid } from 'uuid';

const R = 6371000;
export const haversineM = ([lng1, lat1], [lng2, lat2]) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const dist2D = (a, b) =>
  Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);

export const nearestOnSeg2D = (p, a, b) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { point: a, t: 0, dist: dist2D(p, a) };
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  const point = { x: a.x + t * dx, y: a.y + t * dy };
  return { point, t, dist: dist2D(p, point) };
};

export const polylineLength = (positions) => {
  let len = 0;
  for (let i = 0; i < positions.length - 1; i++) {
    len += haversineM(positions[i], positions[i + 1]);
  }
  return len;
};

export const getPositionAtT = (positions, t) => {
  if (!positions || positions.length === 0) return null;
  if (positions.length === 1) return positions[0];
  if (t <= 0) return positions[0];
  if (t >= 1) return positions[positions.length - 1];

  const segs = [];
  let total = 0;
  for (let i = 0; i < positions.length - 1; i++) {
    const d = haversineM(positions[i], positions[i + 1]);
    segs.push(d);
    total += d;
  }
  if (total === 0) return positions[0];

  const target = t * total;
  let acc = 0;
  for (let i = 0; i < segs.length; i++) {
    if (acc + segs[i] >= target) {
      const frac = segs[i] === 0 ? 0 : (target - acc) / segs[i];
      const a = positions[i];
      const b = positions[i + 1];
      return [a[0] + frac * (b[0] - a[0]), a[1] + frac * (b[1] - a[1])];
    }
    acc += segs[i];
  }
  return positions[positions.length - 1];
};

export const nearestOnPolyline2D = (screenPt, pathPoints, map) => {
  let best = { distPx: Infinity, t: 0, segIndex: 0, position: null };

  const px = pathPoints.map((pt) => map.project(pt.position));
  const segs = [];
  let total = 0;
  for (let i = 0; i < pathPoints.length - 1; i++) {
    const d = haversineM(pathPoints[i].position, pathPoints[i + 1].position);
    segs.push(d);
    total += d;
  }

  let acc = 0;
  for (let i = 0; i < px.length - 1; i++) {
    const { point, t, dist: distPx } = nearestOnSeg2D(screenPt, px[i], px[i + 1]);
    if (distPx < best.distPx) {
      const lngLat = map.unproject(point);
      const globalT = total > 0 ? (acc + t * segs[i]) / total : 0;
      best = { distPx, t: globalT, segIndex: i, position: [lngLat.lng, lngLat.lat] };
    }
    acc += segs[i];
  }

  return best;
};

export const findSnap = ({
  lngLat,
  map,
  paths,
  visiblePins,
  excludePointId = null,
  nodeThresholdPx  = 14,
  segThresholdPx   = 16,
  pinThresholdPx   = 18,
}) => {
  const screenPt = map.project(lngLat);

  for (const pin of (visiblePins || [])) {
    try {
      const pos = parsePinPosition(pin.positions);
      if (!pos) continue;
      const pxPin = map.project(pos);
      if (dist2D(screenPt, pxPin) <= pinThresholdPx) {
        return { type: 'pin', pinId: pin.enc_id, position: pos };
      }
    } catch {  }
  }

  for (const path of paths) {
    for (const pt of path.points) {
      if (pt.id === excludePointId) continue;
      const pxPt = map.project(pt.position);
      if (dist2D(screenPt, pxPt) <= nodeThresholdPx) {
        return { type: 'node', pathId: path.id, pointId: pt.id, position: pt.position };
      }
    }
  }

  let bestSeg = null;
  let bestDist = segThresholdPx;
  for (const path of paths) {
    if (path.points.length < 2) continue;
    const result = nearestOnPolyline2D(screenPt, path.points, map);
    if (result.distPx < bestDist) {
      bestDist = result.distPx;
      bestSeg = {
        type:     'segment',
        pathId:   path.id,
        pathType: path.type,
        segIndex: result.segIndex,
        t:        result.t,
        position: result.position,
      };
    }
  }
  if (bestSeg) return bestSeg;

  return null;
};

export const parsePinPosition = (positions) => {
  try {
    const parsed = typeof positions === 'string' ? JSON.parse(positions) : positions;
    const x = Number(parsed.x);
    const y = Number(parsed.y);
    if (isNaN(x) || isNaN(y)) return null;
    return [x, y];
  } catch {
    return null;
  }
};

export const pathsToLinesGeoJSON = (paths, selectedPathId) => {
  const features = [];
  for (const path of paths) {
    if (path.points.length < 2) continue;
    features.push({
      type: 'Feature',
      id:   path.id,
      geometry: {
        type:        'LineString',
        coordinates: path.points.map((p) => p.position),
      },
      properties: {
        pathId:   path.id,
        pathType: path.type,
        selected: path.id === selectedPathId,
      },
    });
  }
  return { type: 'FeatureCollection', features };
};

export const nodesToGeoJSON = (paths, selectedPathId, selectedPointId, eraserPointIds = null) => {
  const features = [];
  const seen     = new Set();

  for (const path of paths) {
    for (const pt of path.points) {
      if (seen.has(pt.id)) continue;
      seen.add(pt.id);
      features.push({
        type: 'Feature',
        id:   pt.id,
        geometry: { type: 'Point', coordinates: pt.position },
        properties: {
          pointId:      pt.id,
          pathId:       path.id,
          pathType:     path.type,
          pinId:        pt.pinId    || null,
          isPin:        !!pt.pinId,
          isSnap:       !!pt.snapPathId,
           
          selected:     pt.id === selectedPointId || (eraserPointIds != null && eraserPointIds.has(pt.id)),
          pathSelected: path.id === selectedPathId,
        },
      });
    }
  }
  return { type: 'FeatureCollection', features };
};

export const previewToGeoJSON = (inProgress, mousePos) => {
  if (!inProgress || inProgress.points.length === 0) return emptyCol();

  const pathType = inProgress.type;
  const features = [];

  const lineCoords = [...inProgress.points.map((p) => p.position)];
  if (mousePos) lineCoords.push(mousePos);
  if (lineCoords.length >= 2) {
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: lineCoords },
      properties: { pathType },
    });
  }

  for (const pt of inProgress.points) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: pt.position },
      properties: { pathType },
    });
  }

  return { type: 'FeatureCollection', features };
};

export const emptyCol = () => ({ type: 'FeatureCollection', features: [] });

 
export const eraserSegmentsToGeoJSON = (paths, eraserSelectedPoints) => {
  if (!eraserSelectedPoints || eraserSelectedPoints.length === 0) {
    return { type: 'FeatureCollection', features: [] };
  }
 
  const byPath = {};
  for (const { pointId, pathId } of eraserSelectedPoints) {
    if (!byPath[pathId]) byPath[pathId] = new Set();
    byPath[pathId].add(pointId);
  }

  const features = [];
  for (const path of paths) {
    const selectedSet = byPath[path.id];
    if (!selectedSet) continue;
 
    for (let i = 0; i < path.points.length - 1; i++) {
      const a = path.points[i];
      const b = path.points[i + 1];
      if (selectedSet.has(a.id) || selectedSet.has(b.id)) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [a.position, b.position],
          },
          properties: { pathType: path.type },
        });
      }
    }
  }

  return { type: 'FeatureCollection', features };
};

export const decomposeSnapT = (pathPoints, globalT) => {
  if (!pathPoints || pathPoints.length < 2) return { segIndex: 0, localT: 0 };
  const segs = [];
  let total = 0;
  for (let i = 0; i < pathPoints.length - 1; i++) {
    const d = haversineM(pathPoints[i].position, pathPoints[i + 1].position);
    segs.push(d);
    total += d;
  }
  if (total === 0) return { segIndex: 0, localT: 0 };
  const target = Math.max(0, Math.min(total, globalT * total));
  let acc = 0;
  for (let i = 0; i < segs.length; i++) {
    const end = acc + segs[i];
    if (target <= end || i === segs.length - 1) {
      const localT = segs[i] > 0 ? (target - acc) / segs[i] : 0;
      return { segIndex: i, localT: Math.max(0, Math.min(1, localT)) };
    }
    acc = end;
  }
  return { segIndex: segs.length - 1, localT: 1 };
};

export const reAnchorSnapPoint = (newPathPoints, segIndex, localSegT) => {
  const a = newPathPoints[segIndex]?.position;
  const b = newPathPoints[segIndex + 1]?.position;
  if (!a || !b) return null;
  const newPos = [
    a[0] + localSegT * (b[0] - a[0]),
    a[1] + localSegT * (b[1] - a[1]),
  ];
  
  const segs = [];
  let total = 0;
  for (let i = 0; i < newPathPoints.length - 1; i++) {
    const d = haversineM(newPathPoints[i].position, newPathPoints[i + 1].position);
    segs.push(d);
    total += d;
  }
  if (total === 0) return { position: newPos, snapT: 0 };
  let acc = 0;
  for (let i = 0; i < segIndex; i++) acc += segs[i];
  const snapT = (acc + localSegT * (segs[segIndex] || 0)) / total;
  return { position: newPos, snapT };
};


export const makePoint = (position, extra = {}) => ({
  id: uuid(),
  position,
  pinId:      null,
  snapPathId: null,
  snapT:      null,
  ...extra,
});

const SUB_PATH_PENALTY = 1e9;

export const buildNavGraph = (
  paths,
  prioritizeMain  = true,
  allowedVtPinIds = null,
  vtMovementMap   = null,
  floorOrderMap   = null,
) => {
  const nodes = {};
  const adj   = {};
 

  const ensureNode = (id, pos) => {
    if (!nodes[id]) {
      nodes[id] = pos;
      adj[id]   = [];
    }
  };

  const addEdge = (a, b, w) => {
    adj[a].push({ to: b, weight: w });
    adj[b].push({ to: a, weight: w });
  };

  const addDirectedEdge = (from, to, w) => {
    adj[from].push({ to, weight: w });
  };

  for (const path of paths) {
    for (const pt of path.points) {
      ensureNode(pt.id, pt.position);
    }
    
    const subPenalty = (prioritizeMain && path.type === 'sub') ? SUB_PATH_PENALTY : 0;
    for (let i = 0; i < path.points.length - 1; i++) {
      const a = path.points[i];
      const b = path.points[i + 1];
      const w = haversineM(a.position, b.position) + subPenalty;
      addEdge(a.id, b.id, w);
    }
  }
 
  const snapMap = {};

  for (const path of paths) {
    for (const pt of path.points) {
      if (!pt.snapPathId) continue;
      const hostPath = paths.find((p) => p.id === pt.snapPathId);
      if (!hostPath || hostPath.points.length < 2) continue;

      const positions = hostPath.points.map((p) => p.position);
      const segs = [];
      let total = 0;
      for (let i = 0; i < positions.length - 1; i++) {
        const d = haversineM(positions[i], positions[i + 1]);
        segs.push(d);
        total += d;
      }
      const target = pt.snapT * total;
      let acc = 0;
      let segIdx = 0;
      for (let i = 0; i < segs.length; i++) {
        if (acc + segs[i] >= target) { segIdx = i; break; }
        acc += segs[i];
      }

      ensureNode(pt.id, pt.position);

      if (!snapMap[pt.snapPathId])          snapMap[pt.snapPathId]          = {};
      if (!snapMap[pt.snapPathId][segIdx])  snapMap[pt.snapPathId][segIdx]  = [];
      snapMap[pt.snapPathId][segIdx].push({ pt, snapT: pt.snapT });
    }
  }
 
  for (const [hostPathId, segsObj] of Object.entries(snapMap)) {
    const hostPath = paths.find((p) => p.id === hostPathId);
    if (!hostPath) continue;

    for (const [segIdxStr, snaps] of Object.entries(segsObj)) {
      const segIdx = Number(segIdxStr);
      const nodeA  = hostPath.points[segIdx];
      const nodeB  = hostPath.points[segIdx + 1];
      if (!nodeA || !nodeB) continue;
 
      snaps.sort((a, b) => a.snapT - b.snapT);
 
      const chain = [
        { id: nodeA.id, position: nodeA.position },
        ...snaps.map((s) => ({ id: s.pt.id, position: s.pt.position })),
        { id: nodeB.id, position: nodeB.position },
      ];
      for (let i = 0; i < chain.length - 1; i++) {
        const w = haversineM(chain[i].position, chain[i + 1].position);
        addEdge(chain[i].id, chain[i + 1].id, w);
      }
    }
  }
 

  const pinIdToNodeIds = {};
  const pinIdToFloors  = {};
  const nodeIdToFloorId = {};   

  for (const path of paths) {
    for (const pt of path.points) {
      if (!pt.pinId) continue;
      const pid = String(pt.pinId);
      if (!pinIdToNodeIds[pid]) { pinIdToNodeIds[pid] = []; pinIdToFloors[pid] = new Set(); }
      if (!pinIdToNodeIds[pid].includes(pt.id)) {
        pinIdToNodeIds[pid].push(pt.id);
        nodeIdToFloorId[pt.id] = path.floorId;
      }
      pinIdToFloors[pid].add(path.floorId);
    }
  }

  for (const [pinId, nodeIds] of Object.entries(pinIdToNodeIds)) {
    const isVt = (pinIdToFloors[pinId]?.size ?? 0) > 1;
    if (isVt && allowedVtPinIds !== null && !allowedVtPinIds.has(pinId)) continue;

    if (!isVt || !vtMovementMap || !floorOrderMap) {
      
      for (let i = 0; i < nodeIds.length - 1; i++) {
        addEdge(nodeIds[i], nodeIds[i + 1], 0);
      }
      continue;
    }

    const direction = vtMovementMap[pinId] || 'bidirectional'; 

    if (direction === 'bidirectional') {
      for (let i = 0; i < nodeIds.length - 1; i++) {
        addEdge(nodeIds[i], nodeIds[i + 1], 0);
      }
    } else {
      
      const sorted = [...nodeIds].sort((a, b) => {
        const orderA = floorOrderMap[String(nodeIdToFloorId[a])] ?? 0;
        const orderB = floorOrderMap[String(nodeIdToFloorId[b])] ?? 0;
        return orderA - orderB;   
      });
      
      
      for (let i = 0; i < sorted.length - 1; i++) {
        if (direction === 'up') {
          addDirectedEdge(sorted[i], sorted[i + 1], 0);
        } else {
          addDirectedEdge(sorted[i + 1], sorted[i], 0);
        }
      }
    }
  } 

  return { nodes, adj };
};

export const dijkstra = (nodes, adj, startId, endId) => {
  if (!nodes[startId] || !nodes[endId]) return null;
  if (startId === endId) return { distanceM: 0, nodeIds: [startId] };

  const dist = {};
  const prev = {};
  const visited = new Set();
  Object.keys(nodes).forEach((id) => { dist[id] = Infinity; });
  dist[startId] = 0;

  const pq = [{ id: startId, d: 0 }];

  while (pq.length > 0) {
    pq.sort((a, b) => a.d - b.d);
    const { id: u } = pq.shift();
    if (visited.has(u)) continue;
    visited.add(u);
    if (u === endId) break;

    for (const { to, weight } of (adj[u] || [])) {
      if (visited.has(to)) continue;
      const nd = dist[u] + weight;
      if (nd < dist[to]) {
        dist[to] = nd;
        prev[to]  = u;
        pq.push({ id: to, d: nd });
      }
    }
  }

  if (!isFinite(dist[endId])) return null;

  const nodeIds = [];
  let cur = endId;
  while (cur !== undefined) { nodeIds.unshift(cur); cur = prev[cur]; }

  return { distanceM: dist[endId], nodeIds };
};

// old 
// export const findPinNodeId = (paths, pinId) => {
//   for (const path of paths) {
//     for (const pt of path.points) {
//       if (pt.pinId === pinId) return pt.id;
//     }
//   }
//   return null;
// };

// new
export const findPinNodeId = (paths, pinEncId) => {
  const target = String(pinEncId);
  for (const path of paths) {
    for (const pt of path.points) {
      if (pt.pinId != null && String(pt.pinId) === target) return pt.id;
    }
  }
  return null;
};

export const autoGenerateSubPaths = (paths, visiblePins, map, currentFloor) => {
  const mainPaths = paths.filter((p) => p.type === 'main' && p.floorId != null && currentFloor?.enc_id != null && String(p.floorId) === String(currentFloor.enc_id));
  if (mainPaths.length === 0) return { newSubPaths: [] };

  const newSubPaths = [];

  for (const pin of visiblePins) {
    const pinId = pin.enc_id;
 
    const alreadyConnected = paths.some(
      (p) => p.floorId != null && currentFloor?.enc_id != null && String(p.floorId) === String(currentFloor.enc_id)
        && p.points.some((pt) => String(pt.pinId) === String(pinId))
    );
    if (alreadyConnected) continue;

    const pinPos = parsePinPosition(pin.positions);
    if (!pinPos) continue;

    let best = null;
    let bestDistPx = Infinity;
    const screenPin = map.project(pinPos);

    for (const mp of mainPaths) {
      if (mp.points.length < 2) continue;
      const pxPoints = mp.points.map((pt) => map.project(pt.position));

      let acc = 0;
      const segs = [];
      let total = 0;
      for (let i = 0; i < mp.points.length - 1; i++) {
        const d = haversineM(mp.points[i].position, mp.points[i + 1].position);
        segs.push(d);
        total += d;
      }

      for (let i = 0; i < pxPoints.length - 1; i++) {
        const { point, t: segT, dist: dPx } = nearestOnSeg2D(
          screenPin, pxPoints[i], pxPoints[i + 1]
        );
        if (dPx < bestDistPx) {
          bestDistPx = dPx;
          const lngLat  = map.unproject(point);
          const globalT = total > 0 ? (acc + segT * segs[i]) / total : 0;
          best = {
            mainPathId: mp.id,
            globalT,
            snapPos: [lngLat.lng, lngLat.lat],
          };
        }
        acc += segs[i];
      }
    }

    if (!best) continue;

    newSubPaths.push({
      id:   uuid(),
      type: 'sub',
      floorId: currentFloor?.enc_id,
      points: [
        makePoint(pinPos,       { pinId }),
        makePoint(best.snapPos, { snapPathId: best.mainPathId, snapT: best.globalT }),
      ],
    });
  }
 
  return { newSubPaths }; 
  
};


export const findSortedVtPinIds = (paths, startNodeId, allowedVtPinIds) => { 
  let startPos     = null;
  let startFloorId = null;
  outer: for (const path of paths) {
    for (const pt of path.points) {
      if (pt.id === startNodeId) {
        startPos     = pt.position;
        startFloorId = path.floorId;
        break outer;
      }
    }
  }
  if (!startPos || startFloorId == null) return [];
 
  const pinFloors     = {};
  const pinPosOnFloor = {};
  for (const path of paths) {
    for (const pt of path.points) {
      if (!pt.pinId) continue;
      const pid = String(pt.pinId);
      if (!pinFloors[pid])     { pinFloors[pid] = new Set(); pinPosOnFloor[pid] = {}; }
      pinFloors[pid].add(String(path.floorId));
      pinPosOnFloor[pid][String(path.floorId)] = pt.position;
    }
  }

  const candidates = [];
  for (const [pinId, floors] of Object.entries(pinFloors)) {
    if (floors.size <= 1) continue; 
    if (allowedVtPinIds !== null && !allowedVtPinIds.has(pinId)) continue; 
    const posOnStart = pinPosOnFloor[pinId]?.[String(startFloorId)];
    if (!posOnStart) continue; 
    candidates.push({ pinId, dist: haversineM(startPos, posOnStart) });
  }

  candidates.sort((a, b) => a.dist - b.dist);
  return candidates.map((c) => c.pinId);
};
 